import React, { createContext, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import MainRoutes from '@/v2/routes/MainRoutes.jsx'
import ModalRoutes from '@/v2/routes/ModalRoutes.jsx'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import { withClaimIdParam } from '@/v2/utils/claimDeepLink'

export const ModalHistoryContext = createContext({})

const MODAL_PATTERNS = [
  '/check-in',
  '/event',
  '/appointment',
  '/time-off',
  '/new-patient',
  '/documents',
  '/finance',
  '/notes',
  '/edit',
  '/basic-info',
  '/contact-info',
  '/emergency-contact',
  '/imaging',
  '/medical-alerts',
  '/insurance',
  '/eligibility',
  '/claim',
  '/task',
  '/crop-image',
  '/seat-patient',
  '/consent-form',
  '/create-contract',
  '/contract-details',
]

// Helper to deep clean state of non-serializable values
const cleanStateForHistory = obj => {
  if (obj === null || obj === undefined) return obj

  const type = typeof obj

  // Return primitives as-is
  if (type === 'string' || type === 'number' || type === 'boolean') {
    return obj
  }

  // Filter out functions, symbols, undefined
  if (type === 'function' || type === 'symbol' || type === 'undefined') {
    return undefined
  }

  // Handle Date objects - convert to ISO string to preserve across history
  if (obj instanceof Date) {
    return obj.toISOString()
  }

  // Handle File and Blob objects - keep them as-is for image uploads
  if (obj instanceof File || obj instanceof Blob) {
    return obj
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(cleanStateForHistory).filter(item => item !== undefined)
  }

  // Handle objects
  if (type === 'object') {
    const cleaned = {}
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanStateForHistory(value)
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue
      }
    }
    return cleaned
  }

  return obj
}

// Routes that should be fullscreen on mobile instead of modal
const MOBILE_FULLSCREEN_PATTERNS = [
  '/appointment',
  '/event',
  '/edit/basic',
  '/edit/contact-info',
]

const PATIENT_TAB_PATTERN = /^\/v2\/patient\/\d+\/(profile|treatment|insurance|documents|imaging|finance)(\/progression\/\d+\/series\/\d+)?$/

const isModalOnlyPathname = pathname =>
  !PATIENT_TAB_PATTERN.test(pathname) &&
  !pathname.includes('/insurance-form') &&
  !pathname.includes('/health-history-form') &&
  !pathname.startsWith('/v2/settings') &&
  MODAL_PATTERNS.some(pattern => new RegExp(`${pattern}(/|$|-)`).test(pathname))

const defaultBackgroundLocation = () => ({
  pathname: '/v2/calendar/month/' + new Date().toISOString().slice(0, 7),
})

export const ModalProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useRef(location)
  const { isMobile } = useDetectDevice()

  // Keep location ref updated
  useEffect(() => {
    locationRef.current = location
  }, [location])

  const history = useMemo(
    () => ({
      push: (path, state) => {
        // Check if this path should be fullscreen on mobile
        const isMobileFullscreenPath =
          isMobile &&
          MOBILE_FULLSCREEN_PATTERNS.some(pattern => {
            const regex = new RegExp(`${pattern}(/|$|-)`)
            return regex.test(path)
          })

        const isModalPath = !isMobileFullscreenPath && isModalOnlyPathname(path)

        if (isModalPath) {
          const cleanState = cleanStateForHistory(state || {})
          const currentLocation = locationRef.current
          const backgroundLocation =
            currentLocation.state?.backgroundLocation ||
            (isModalOnlyPathname(currentLocation.pathname)
              ? defaultBackgroundLocation()
              : currentLocation)

          const targetPath = withClaimIdParam(path, state)

          navigate(targetPath, {
            state: {
              ...cleanState,
              backgroundLocation,
            },
          })
        } else {
          navigate(path, { state: cleanStateForHistory(state) })
        }
      },
      replace: (path, state) =>
        navigate(path, { replace: true, state: cleanStateForHistory(state) }),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      get location() {
        // Return current location dynamically instead of memoizing it
        return locationRef.current
      },
    }),
    [navigate, isMobile]
  )

  // Check if this path should be fullscreen on mobile
  const isMobileFullscreenPath =
    isMobile &&
    MOBILE_FULLSCREEN_PATTERNS.some(pattern => {
      const regex = new RegExp(`${pattern}(/|$|-)`)
      return regex.test(location.pathname)
    })

  const isModalPath =
    !isMobileFullscreenPath && isModalOnlyPathname(location.pathname)

  // When on a modal path, use the background location from state, or fallback to a sensible default
  const backgroundLocation = isModalPath
    ? location.state?.backgroundLocation || defaultBackgroundLocation()
    : location

  return (
    <ModalHistoryContext.Provider value={history}>
      {children(
        <>
          <MainRoutes location={backgroundLocation} />
          {isModalPath && <ModalRoutes />}
        </>
      )}
    </ModalHistoryContext.Provider>
  )
}
